/**
 * Sales Plan Workbook — Manager-only.
 * Mirrors Bryan's QBR Excel template: per AE, per year, annual quota + Q1-Q4 + 12 months.
 *
 * Locked rules (May 5, 2026):
 *   - Plan numbers entered by MANAGER only. AE cannot edit.
 *   - Pipeline weighting set at MANAGER level via forecast_weights (not on this page).
 *   - 4-color attainment thresholds: green ≥95% / yellow 80-94% / amber 60-79% / red <60%
 *
 * UX:
 *   - Left rail: list of AEs (and BDRs). Click to open their plan.
 *   - Right pane: workbook for selected AE — annual quota, Q1-Q4 row, 12 monthly cells.
 *   - Auto-balance helpers: split annual evenly across quarters / months.
 *   - Quarter cells auto-recompute from monthly totals so manager sees the truth.
 *   - Save sends POST /api/sales-plan; success toast confirms. On failure, button turns
 *     red briefly and an error message is shown — save never fails silently.
 *
 * Tenant isolation: team list fetched from /api/ae-users which is scoped to the
 * calling user's tenant. The id returned is the user's UUID string, which is what
 * the Zod schema on /api/sales-plan requires.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api } from "@/lib/session";
import { Link } from "wouter";
import { useSprintConfig } from "@/lib/useSprintConfig";
import { useUnloadWarning } from "@/lib/useUnloadWarning";

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isDualRole?: boolean;
}

interface SalesPlan {
  id?: string;
  ae_user_id: string;
  plan_year: number;
  annual_quota: number;
  q1: number; q2: number; q3: number; q4: number;
  m01: number; m02: number; m03: number;
  m04: number; m05: number; m06: number;
  m07: number; m08: number; m09: number;
  m10: number; m11: number; m12: number;
  notes?: string | null;
}

const MONTH_KEYS = ["m01","m02","m03","m04","m05","m06","m07","m08","m09","m10","m11","m12"] as const;
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function emptyPlan(aeId: string, year: number): SalesPlan {
  return {
    ae_user_id: aeId,
    plan_year: year,
    annual_quota: 0,
    q1: 0, q2: 0, q3: 0, q4: 0,
    m01: 0, m02: 0, m03: 0, m04: 0, m05: 0, m06: 0,
    m07: 0, m08: 0, m09: 0, m10: 0, m11: 0, m12: 0,
    notes: "",
  };
}

function fmtMoney(v: number): string {
  if (!v) return "";
  return v.toLocaleString("en-US");
}

function parseMoney(s: string): number {
  const n = Number(s.replace(/[^0-9.\-]/g, ""));
  return isFinite(n) ? n : 0;
}

export default function SalesPlanWorkbook() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [plan, setPlan] = useState<SalesPlan | null>(null);
  const [savedSignature, setSavedSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false); // true = button turns red
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const planSignature = plan ? JSON.stringify(plan) : null;
  const isDirty = !!planSignature && planSignature !== savedSignature;
  useUnloadWarning(
    isDirty,
    "This sales plan has unsaved edits. Click Save before leaving."
  );

  // Load AE/BDR list — /api/ae-users is tenant-scoped and returns real UUID strings.
  // Do NOT use /api/users here: that endpoint calls storage.listUsers() which
  // is hardcoded to TENANT_ID=1 and would show the wrong tenant's team.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api<UserRow[]>("/api/ae-users");
        if (cancelled) return;
        setUsers(list);
        if (!selectedId && list.length > 0) setSelectedId(list[0].id);
      } catch (e: any) {
        setError(e?.message ?? "Could not load team list");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load selected AE plan
  useEffect(() => {
    if (!selectedId) { setPlan(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await api<SalesPlan | null>(`/api/sales-plan/${selectedId}?year=${year}`);
        if (cancelled) return;
        const loaded = data ?? emptyPlan(selectedId, year);
        setPlan(loaded);
        setSavedSignature(JSON.stringify(loaded));
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "Could not load plan");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, year]);

  const monthlyTotal = useMemo(() => {
    if (!plan) return 0;
    return MONTH_KEYS.reduce((s, k) => s + (Number(plan[k]) || 0), 0);
  }, [plan]);

  const computedQuarters = useMemo(() => {
    if (!plan) return { q1: 0, q2: 0, q3: 0, q4: 0 };
    const m = (k: typeof MONTH_KEYS[number]) => Number(plan[k]) || 0;
    return {
      q1: m("m01") + m("m02") + m("m03"),
      q2: m("m04") + m("m05") + m("m06"),
      q3: m("m07") + m("m08") + m("m09"),
      q4: m("m10") + m("m11") + m("m12"),
    };
  }, [plan]);

  function setField<K extends keyof SalesPlan>(k: K, v: SalesPlan[K]) {
    if (!plan) return;
    setPlan({ ...plan, [k]: v });
  }

  function splitEvenAcrossMonths() {
    if (!plan) return;
    const per = Math.round(plan.annual_quota / 12);
    const next = { ...plan };
    MONTH_KEYS.forEach((k) => { (next as any)[k] = per; });
    // sync quarters
    next.q1 = per * 3; next.q2 = per * 3; next.q3 = per * 3; next.q4 = per * 3;
    setPlan(next);
  }

  function splitEvenAcrossQuarters() {
    if (!plan) return;
    const perQ = Math.round(plan.annual_quota / 4);
    const perM = Math.round(perQ / 3);
    const next = { ...plan, q1: perQ, q2: perQ, q3: perQ, q4: perQ };
    MONTH_KEYS.forEach((k) => { (next as any)[k] = perM; });
    setPlan(next);
  }

  function clearAll() {
    if (!plan || !selectedId) return;
    setPlan(emptyPlan(selectedId, year));
  }

  async function save() {
    if (!plan) return;
    setSaving(true);
    setSaveError(false);
    setError(null);
    try {
      // ae_user_id must be a UUID string. /api/ae-users always returns the
      // real UUID from the users table, so plan.ae_user_id is already correct.
      // Send computed quarters so they always match the monthly totals.
      const body = {
        ...plan,
        q1: computedQuarters.q1,
        q2: computedQuarters.q2,
        q3: computedQuarters.q3,
        q4: computedQuarters.q4,
      };
      const saved = await api<SalesPlan>("/api/sales-plan", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });
      setPlan(saved);
      setSavedSignature(JSON.stringify(saved));
      setSavedAt(new Date());
    } catch (e: any) {
      // Surface every error — never return to idle on a failed save.
      const msg =
        e?.body?.error?.fieldErrors
          ? Object.entries(e.body.error.fieldErrors as Record<string, string[]>)
              .map(([f, errs]) => `${f}: ${errs.join(", ")}`)
              .join("; ")
          : (e?.message ?? "Could not save plan");
      setError(msg);
      setSaveError(true);
      // Reset red state after 3 s so the button reverts to normal.
      setTimeout(() => setSaveError(false), 3000);
    } finally {
      setSaving(false);
    }
  }

  const selectedUser = users.find((u) => u.id === selectedId);
  const variance = monthlyTotal - (plan?.annual_quota ?? 0);
  const varianceColor =
    Math.abs(variance) < 1 ? "#065F46" :
    Math.abs(variance) < (plan?.annual_quota ?? 0) * 0.05 ? "#92400E" : "#991B1B";

  return (
    <div style={{ minHeight: "100vh", background: "#F9FAFB", padding: "24px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
              Manager — Sales Plan Workbook
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 600, color: "#111827", margin: 0 }}>
              Plan numbers, by AE
            </h1>
            <div style={{ fontSize: 14, color: "#6B7280", marginTop: 6, maxWidth: 720 }}>
              Annual quota, quarterly targets, monthly distribution. AEs see these on their Plan vs Actual page but cannot edit. Pipeline weighting is set elsewhere at the manager level.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <label style={{ fontSize: 12, color: "#6B7280" }}>Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              style={{ padding: "6px 10px", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 14, background: "#fff" }}
            >
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Link href="/pilot">
              <a style={{ fontSize: 13, color: "#374151", textDecoration: "none", padding: "6px 12px", border: "1px solid #D1D5DB", borderRadius: 6, background: "#fff" }}>
                Back to home
              </a>
            </Link>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20 }}>
          {/* Left rail — AE list */}
          <aside style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 12, height: "fit-content" }}>
            <div style={{ fontSize: 11, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase", padding: "4px 8px 8px" }}>
              Team ({users.length})
            </div>
            {users.length === 0 && (
              <div style={{ padding: 12, fontSize: 13, color: "#9CA3AF" }}>No AEs or BDRs yet.</div>
            )}
            {users.map((u) => {
              const isSelected = u.id === selectedId;
              return (
                <button
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  style={{
                    width: "100%", textAlign: "left", padding: "10px 12px",
                    background: isSelected ? "#F5F5F4" : "transparent",
                    border: "none", borderRadius: 6, cursor: "pointer",
                    marginBottom: 2, display: "flex", flexDirection: "column", gap: 2,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500, color: isSelected ? "#0B0B0F" : "#111827" }}>
                    {u.name || u.email}
                  </span>
                  <span style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {u.role}{u.isDualRole ? " · dual" : ""}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Right pane — workbook */}
          <main style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 24 }}>
            {loading && <div style={{ color: "#6B7280", fontSize: 14 }}>Loading…</div>}
            {error && (
              <div style={{ background: "#FEF2F2", color: "#991B1B", padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {!loading && plan && selectedUser && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 20, fontWeight: 600, color: "#111827" }}>
                    {selectedUser.name || selectedUser.email}
                  </div>
                  <div style={{ fontSize: 13, color: "#6B7280" }}>
                    Plan year {plan.plan_year} · {selectedUser.role.toUpperCase()}
                  </div>
                </div>

                <SprintBoySplit aeUserId={selectedUser.id} />

                {/* Annual quota */}
                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                    Annual quota (USD)
                  </label>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 24, color: "#6B7280" }}>$</span>
                    <input
                      type="text"
                      value={fmtMoney(plan.annual_quota)}
                      onChange={(e) => setField("annual_quota", parseMoney(e.target.value))}
                      placeholder="0"
                      style={{
                        flex: 1, fontSize: 24, fontWeight: 600, color: "#111827",
                        border: "none", outline: "none", background: "transparent",
                        padding: "4px 0",
                      }}
                    />
                    <button
                      onClick={splitEvenAcrossMonths}
                      style={{ fontSize: 12, padding: "6px 10px", border: "1px solid #D1D5DB", borderRadius: 6, background: "#fff", cursor: "pointer", color: "#374151" }}
                    >
                      Split evenly · 12 months
                    </button>
                    <button
                      onClick={splitEvenAcrossQuarters}
                      style={{ fontSize: 12, padding: "6px 10px", border: "1px solid #D1D5DB", borderRadius: 6, background: "#fff", cursor: "pointer", color: "#374151" }}
                    >
                      Split evenly · 4 quarters
                    </button>
                  </div>
                </div>

                {/* Quarters (computed, read-only) */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                    Quarterly targets (computed from monthly)
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                    {(["q1","q2","q3","q4"] as const).map((q) => (
                      <div key={q} style={{ background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: 6, padding: 12 }}>
                        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {q.toUpperCase()}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: "#111827", marginTop: 4 }}>
                          ${fmtMoney(computedQuarters[q]) || 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Monthly grid */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                    Monthly distribution
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
                    {MONTH_KEYS.map((k, idx) => (
                      <div key={k} style={{ background: "#fff", border: "1px solid #D1D5DB", borderRadius: 6, padding: "8px 10px" }}>
                        <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {MONTH_LABELS[idx]}
                        </div>
                        <input
                          type="text"
                          value={fmtMoney(plan[k] as number)}
                          onChange={(e) => setField(k, parseMoney(e.target.value) as any)}
                          placeholder="0"
                          style={{
                            width: "100%", fontSize: 14, fontWeight: 500, color: "#111827",
                            border: "none", outline: "none", background: "transparent",
                            padding: "2px 0", marginTop: 2,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Variance summary */}
                <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 6, padding: 12, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, color: "#374151" }}>
                    Monthly sum: <strong>${fmtMoney(monthlyTotal)}</strong>
                    <span style={{ color: "#9CA3AF", margin: "0 8px" }}>vs</span>
                    Annual quota: <strong>${fmtMoney(plan.annual_quota)}</strong>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: varianceColor }}>
                    {variance === 0 ? "Balanced" : variance > 0 ? `Over by $${fmtMoney(variance)}` : `Short by $${fmtMoney(-variance)}`}
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                    Notes (optional)
                  </label>
                  <textarea
                    value={plan.notes ?? ""}
                    onChange={(e) => setField("notes", e.target.value)}
                    rows={3}
                    placeholder="Context behind these numbers — comp plan changes, ramp timing, territory shifts."
                    style={{ width: "100%", padding: 10, border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
                  />
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 10, alignItems: "center", borderTop: "1px solid #E5E7EB", paddingTop: 16 }}>
                  <button
                    onClick={save}
                    disabled={saving}
                    style={{
                      padding: "10px 20px", fontSize: 14, fontWeight: 600,
                      background: saving ? "#9CA3AF" : saveError ? "#DC2626" : "#111827",
                      color: "#fff",
                      border: "none", borderRadius: 6,
                      cursor: saving ? "default" : "pointer",
                      transition: "background 0.2s",
                    }}
                  >
                    {saving ? "Saving…" : saveError ? "Save failed" : "Save plan"}
                  </button>
                  <button
                    onClick={clearAll}
                    style={{ padding: "10px 16px", fontSize: 14, background: "#fff", color: "#374151", border: "1px solid #D1D5DB", borderRadius: 6, cursor: "pointer" }}
                  >
                    Clear all
                  </button>
                  {savedAt && (
                    <span style={{ fontSize: 12, color: "#065F46", marginLeft: 8 }}>
                      Saved at {savedAt.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

interface SprintBoySplitProps { aeUserId: string }

function SprintBoySplit({ aeUserId }: SprintBoySplitProps) {
  const { window: w } = useSprintConfig();
  const [sprintGoal, setSprintGoal] = useState<string>("0");
  const [boyGoal, setBoyGoal] = useState<string>("0");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api<{ sprint_goal: number; boy_goal: number }>(`/api/sales-plan/sprint?ae_user_id=${aeUserId}`);
        if (!cancelled) {
          setSprintGoal(String(r.sprint_goal ?? 0));
          setBoyGoal(String(r.boy_goal ?? 0));
        }
      } catch {
        // unauthenticated or endpoint missing — show zeros
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [aeUserId]);

  async function save() {
    setError(null);
    try {
      await api("/api/sales-plan/sprint", {
        method: "PUT",
        body: JSON.stringify({
          ae_user_id: aeUserId,
          sprint_goal: Number(sprintGoal) || 0,
          boy_goal: Number(boyGoal) || 0,
        }),
      });
      setSavedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? "Save failed");
    }
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Sales Plan" }]} pageTitle="Sales Plan Workbook" pageMeta="Manager planning">
      <div style={{ fontSize: 11, color: "#92400E", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
        Sprint vs Balance of year
      </div>
      <div style={{ fontSize: 12, color: "#78350F", marginBottom: 12 }}>
        Sprint window: {w?.sprintStart ?? "—"} - {w?.sprintEnd ?? "—"} ({w?.daysRemaining ?? 0} days remaining)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
        <div>
          <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Sprint goal (USD)</label>
          <input
            type="text"
            value={sprintGoal}
            onChange={(e) => setSprintGoal(e.target.value.replace(/[^0-9]/g, ""))}
            disabled={loading}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, border: "1px solid #D1D5DB", borderRadius: 6 }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Balance of year goal (USD)</label>
          <input
            type="text"
            value={boyGoal}
            onChange={(e) => setBoyGoal(e.target.value.replace(/[^0-9]/g, ""))}
            disabled={loading}
            style={{ width: "100%", padding: "8px 10px", fontSize: 14, border: "1px solid #D1D5DB", borderRadius: 6 }}
          />
        </div>
        <button
          onClick={save}
          disabled={loading}
          style={{ padding: "10px 16px", fontSize: 13, background: "#0B0B0F", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
        >
          Save sprint
        </button>
      </div>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: "#991B1B" }}>{error}</div>}
      {savedAt && !error && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#065F46" }}>
          Saved at {savedAt.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </OperatorShell>
  );
}
